import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/api';
import DOMPurify from 'dompurify';
import QuillEditor from './QuillEditor';

export default function CurrentAnalysisNotes({ 
  analysisParams,  // Now expects meta object directly
  temporaryAnalysis,
  darkMode = false,
  className = '',
}) {
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  
  const saveTimeoutRef = useRef(null);
  const lastAnalysisParamsRef = useRef(null);
  
  // Word count limit
  const WORD_LIMIT = 500;
  
  // Calculate word count from HTML content
  const getWordCount = useCallback((htmlContent) => {
    if (!htmlContent) return 0;
    
    // Strip HTML tags and get plain text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    // Count words (split by whitespace and filter empty strings)
    const words = plainText.trim().split(/\s+/).filter(word => word.length > 0);
    return words.length;
  }, []);
  
  // Get current word count
  const currentWordCount = getWordCount(notes);
  const isOverLimit = currentWordCount > WORD_LIMIT;
  
  // Sanitize notes content to prevent XSS and handle special characters
  const sanitizeNotes = useCallback((content) => {
    if (!content || typeof content !== 'string') {
      return '';
    }
    
    // Use DOMPurify to sanitize HTML content
    // Allow basic formatting tags that ReactQuill uses
    const cleanContent = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote'],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM_IMPORT: false
    });
    
    return cleanContent;
  }, []);
  
  // Extract analysis parameters from meta object
  const getAnalysisParamsFromMeta = useCallback((meta) => {
    if (!meta?.domain || !meta?.dateRange?.periodOne || !meta?.dateRange?.periodTwo) {
      return null;
    }
    
    return {
      property_url: meta.domain,
      period_1: {
        start: meta.dateRange.periodOne.startDate,
        end: meta.dateRange.periodOne.endDate
      },
      period_2: {
        start: meta.dateRange.periodTwo.startDate,
        end: meta.dateRange.periodTwo.endDate
      }
    };
  }, []);
  
  // Fetch notes from API
  const fetchNotes = useCallback(async (meta) => {
    if (!meta) return;
    
    const params = getAnalysisParamsFromMeta(meta);
    if (!params) return;
    
    setIsLoading(true);
    setFetchError(null);
    
    try {
      // Manually construct URL with query parameters for GET request
      const queryParams = new URLSearchParams({
        property_url: params.property_url,
        period_1_start: params.period_1.start,
        period_1_end: params.period_1.end,
        period_2_start: params.period_2.start,
        period_2_end: params.period_2.end,
      });
      
      const url = `/api/analyze/history/notes/get/?${queryParams.toString()}`;
      
      const response = await apiRequest(url, {
        method: 'GET'
      });
      
      // Parse the JSON response
      if (response.ok) {
        const data = await response.json();
        
        if (data.notes) {
          setNotes(sanitizeNotes(data.notes));
        } else {
          // No existing notes found, start with empty form
          setNotes('');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch notes (${response.status})`);
      }
    } catch (error) {
      setFetchError(error.message);
      // Start with empty form on error
      setNotes('');
    } finally {
      setIsLoading(false);
    }
  }, [getAnalysisParamsFromMeta, sanitizeNotes]);

  // Save notes to API
  const saveNotes = useCallback(async (content, meta) => {
    if (!meta || !content.trim()) {
      return;
    }
    
    const params = getAnalysisParamsFromMeta(meta);
    if (!params) return;
    
    // Check word count before saving
    const wordCount = getWordCount(content);
    if (wordCount > WORD_LIMIT) {
      setSaveError(`Notes are too long (${wordCount} words). Please keep under ${WORD_LIMIT} words.`);
      return;
    }
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Sanitize content before saving
      const sanitizedContent = sanitizeNotes(content);
      
      const requestData = {
        property_url: params.property_url,
        period_1: params.period_1,
        period_2: params.period_2,
        notes: sanitizedContent
      };
      
      const response = await apiRequest('/api/analyze/history/notes/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      
      if (response.ok) {
        setLastSaved(new Date());
        setSaveError(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to save notes (${response.status})`);
      }
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setIsSaving(false);
    }
  }, [sanitizeNotes, getWordCount, getAnalysisParamsFromMeta]);
  
  // Fetch notes when analysis parameters change
  useEffect(() => {
    if (analysisParams) {
      fetchNotes(analysisParams);
    }
  }, [analysisParams, fetchNotes]);
  
  // Debounced auto-save
  const debouncedSave = useCallback((content) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      if (analysisParams && content.trim()) {
        // Check word count before auto-saving
        const wordCount = getWordCount(content);
        if (wordCount <= WORD_LIMIT) {
          saveNotes(content, analysisParams);
        } else {
          setSaveError(`Notes are too long (${wordCount} words). Please keep under ${WORD_LIMIT} words.`);
        }
      }
    }, 2000); // Save 2 seconds after user stops typing
  }, [analysisParams, saveNotes, getWordCount]);
  
  // Handle notes change
  const handleNotesChange = (content) => {
    setNotes(content);
    setSaveError(null); // Clear any previous save errors when user types
    debouncedSave(content);
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  
  // Show loading state or no analysis message
  if (!analysisParams) {
    return (
      <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <DocumentTextIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No analysis loaded</p>
        <p className="text-xs mt-1">Run an analysis to add notes</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className={`
          h-4 w-4 animate-spin rounded-full border border-t-transparent mx-auto mb-2
          ${darkMode ? 'border-blue-400' : 'border-blue-500'}
        `} />
        <p className="text-sm">Loading notes...</p>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="space-y-3">
        {/* Fetch Error Banner */}
        {fetchError && (
          <div className={`
            p-3 rounded-lg border text-sm
            ${darkMode 
              ? 'border-red-600 bg-red-900/20 text-red-400' 
              : 'border-red-300 bg-red-50 text-red-600'
            }
          `}>
            <p className="font-medium">Failed to load notes</p>
            <p className="text-xs mt-1">{fetchError}</p>
          </div>
        )}

        {/* Notes Editor */}
        <div className={`
          rounded-lg border overflow-hidden
          ${darkMode 
            ? 'border-gray-600 bg-gray-800' 
            : 'border-gray-300 bg-white'
          }
        `}>
          <QuillEditor
            value={notes}
            onChange={handleNotesChange}
            placeholder="Add your analysis notes here... Document insights, observations, and action items as you work through the data."
            darkMode={darkMode}
          />
        </div>
        
        {/* Save Status */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            {isSaving && (
              <div className="flex items-center gap-1">
                <div className={`
                  h-3 w-3 animate-spin rounded-full border border-t-transparent
                  ${darkMode ? 'border-blue-400' : 'border-blue-500'}
                `} />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                  Saving...
                </span>
              </div>
            )}
            
            {!isSaving && lastSaved && !saveError && (
              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                Last saved: {lastSaved.toLocaleTimeString()}
              </span>
            )}
            
            {saveError && (
              <span className="text-red-500 text-xs">
                Save failed: {saveError}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* Word Count Indicator */}
            <span className={`
              text-xs font-medium
              ${isOverLimit 
                ? 'text-red-500' 
                : currentWordCount > WORD_LIMIT * 0.8 
                  ? darkMode ? 'text-yellow-400' : 'text-yellow-600'
                  : darkMode ? 'text-gray-500' : 'text-gray-400'
              }
            `}>
              {currentWordCount}/{WORD_LIMIT} words
            </span>
            
            <span className={`
              text-xs
              ${darkMode ? 'text-gray-500' : 'text-gray-400'}
            `}>
              Auto-saves while typing
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}